import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import LinkButton from './LinkButton';

const Sidebar = ({ events }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('');
  const [selectedPromptCategory, setSelectedPromptCategory] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setIsOpen(false); // Close mobile menu when resizing to desktop
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['organization', 'studying', 'extracurricular', 'applying-to-college', 'faq'];
      const scrollPosition = window.scrollY + 250;

      let foundSection = '';
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            foundSection = section;
            break;
          }
        }
      }
      setActiveSection(foundSection);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);

  useEffect(() => {
    const handlePromptCategorySelected = (event) => {
      setSelectedPromptCategory(event.detail);
    };

    window.addEventListener('promptCategorySelected', handlePromptCategorySelected);
    return () => window.removeEventListener('promptCategorySelected', handlePromptCategorySelected);
  }, []);

  const handleButtonClick = (section) => {
    if (location.pathname === '/prompt-library') {
      // Navigate to prompt library with the category
      navigate('/prompt-library', { state: { openCategory: section } });
      // Trigger event to open modal
      window.dispatchEvent(new CustomEvent('openPromptCategory', { detail: section }));
    } else if (location.pathname === '/tutorials') {
      // Scroll to tutorial section
      const element = document.getElementById(section);
      if (element) {
        const yOffset = -100;
        const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    } else {
      // Default home page behavior
      const element = document.getElementById(section);
      if (element) {
        const yOffset = -100;
        const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }
    
    // Close mobile menu after clicking
    if (isMobile) {
      setIsOpen(false);
    }
  };

  const getButtonConfig = () => {
    if (location.pathname === '/prompt-library') {
      return [
        { id: 'emails', label: 'Emails', gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
        { id: 'studying', label: 'Studying', gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' },
        { id: 'extracurricular', label: 'Extracurricular', gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
        { id: 'applying-to-college', label: 'Applying To College', gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' },
        { id: 'sat-act', label: 'SAT | ACT', gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }
      ];
    } else if (location.pathname === '/tutorials') {
      return [
        { id: 'google-workspace', label: 'Google Workspace', gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' },
        { id: 'studying-learning', label: 'Studying & Learning', gradient: 'linear-gradient(135deg, #ff6a00 0%, #ee0979 100%)' },
        { id: 'content-creation', label: 'Content Creation', gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' },
        { id: 'research-writing', label: 'Research & Writing', gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)' },
        { id: 'college-applications', label: 'College Applications', gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)' }
      ];
    } else {
      return [
        { id: 'organization', label: 'Organization', gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' },
        { id: 'studying', label: 'Studying', gradient: 'linear-gradient(135deg, #ff6a00 0%, #ee0979 100%)' },
        { id: 'extracurricular', label: 'Extracurricular Activities', gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' },
        { id: 'applying-to-college', label: 'Applying to College', gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)' },
        { id: 'faq', label: 'FAQ', gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)' }
      ];
    }
  };

  const buttons = getButtonConfig();

  const isButtonActive = (buttonId) => {
    if (location.pathname === '/prompt-library') {
      return selectedPromptCategory === buttonId;
    } else if (location.pathname === '/tutorials') {
      return activeSection === buttonId;
    } else {
      return activeSection === buttonId;
    }
  };

  return (
    <>
      {/* Mobile Toggle Button */}
      {isMobile && (
        <button
          onClick={() => setIsOpen(!isOpen)}
          style={{
            position: 'fixed',
            top: '90px',
            right: '1rem',
            zIndex: 1100,
            backgroundColor: 'white',
            border: '2px solid #ddd',
            borderRadius: '8px',
            padding: '0.75rem',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
            transition: 'all 0.3s'
          }}
        >
          <div style={{ width: '24px', height: '3px', backgroundColor: '#2c3e50', borderRadius: '2px', transition: 'all 0.3s', transform: isOpen ? 'rotate(45deg) translateY(7px)' : 'none' }}></div>
          <div style={{ width: '24px', height: '3px', backgroundColor: '#2c3e50', borderRadius: '2px', transition: 'all 0.3s', opacity: isOpen ? '0' : '1' }}></div>
          <div style={{ width: '24px', height: '3px', backgroundColor: '#2c3e50', borderRadius: '2px', transition: 'all 0.3s', transform: isOpen ? 'rotate(-45deg) translateY(-7px)' : 'none' }}></div>
        </button>
      )}

      {/* Overlay for mobile */}
      {isMobile && isOpen && (
        <div
          onClick={() => setIsOpen(false)}
          style={{
            position: 'fixed',
            top: '80px',
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 999,
            transition: 'opacity 0.3s'
          }}
        ></div>
      )}

      {/* Sidebar */}
      <div style={{
        position: 'fixed',
        top: '80px',
        right: isMobile ? (isOpen ? 0 : '-100%') : 0,
        width: isMobile ? '85%' : '450px',
        maxWidth: isMobile ? '400px' : 'none',
        height: 'calc(100vh - 80px)',
        backgroundColor: 'white',
        overflowY: 'auto',
        transition: 'right 0.3s ease-in-out',
        zIndex: 1000,
        boxShadow: isMobile ? '-2px 0 8px rgba(0,0,0,0.1)' : 'none'
      }}>
        <div style={{
          position: 'absolute',
          left: 0,
          top: '20px',
          bottom: '20px',
          width: '1px',
          backgroundColor: '#ddd'
        }}></div>
        <div style={{ padding: '2rem', paddingTop: '2rem' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '3rem' }}>
          {buttons.map((button) => (
            <button
              key={button.id}
              onClick={() => handleButtonClick(button.id)}
              style={{
                width: '100%',
                padding: '1rem 0',
                backgroundColor: 'transparent',
                background: 'none',
                border: 'none',
                textAlign: 'left',
                fontSize: '1.3rem',
                fontWeight: 700,
                fontFamily: 'Inter, sans-serif',
                cursor: 'pointer',
                transition: 'all 0.3s',
                backgroundImage: isButtonActive(button.id)
                  ? button.gradient
                  : 'linear-gradient(135deg, #aaa 0%, #888 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}
            >
              {button.label}
            </button>
          ))}
        </div>
        
        <h2 style={{
          color: '#2c3e50',
          marginBottom: '1rem',
          fontSize: '1.5rem',
          fontWeight: '750',
          textAlign: 'left'
        }}>
          Upcoming Events
        </h2>
        
        <p style={{
          color: '#6c757d',
          lineHeight: '1.2',
          marginBottom: '2rem',
          fontSize: '.9rem',
          textAlign: 'left'
        }}>
          Hear from a panel of students, teachers, and us to help guide you and your child through navigating this new landscape
        </p>
        
        <div>
          {events.length > 0 ? (
            <>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0',
                marginBottom: '1.5rem'
              }}>
                {events.slice(0, 3).map((event) => (
                  <a
                    key={event.id}
                    href="https://rorossssss.app.n8n.cloud/form/f995fefc-885b-4af9-9336-60402602d894"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      textDecoration: 'none',
                      backgroundColor: 'transparent',
                      padding: '1rem 0',
                      borderBottom: '1px solid #ddd',
                      cursor: 'pointer',
                      transition: 'opacity 0.3s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                  >
                    <div style={{
                      fontSize: '1rem',
                      fontWeight: '600',
                      color: '#2c3e50',
                      marginBottom: '0.75rem'
                    }}>
                      {event.location}
                    </div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '30px'
                    }}>
                      <div style={{
                        fontSize: '0.85rem',
                        color: '#6c757d'
                      }}>
                        {event.date}
                      </div>
                      <div style={{
                        fontSize: '0.85rem',
                        color: '#6c757d'
                      }}>
                        {event.time}
                      </div>
                    </div>
                  </a>
                ))}
              </div>
              
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                padding: '1rem 0'
              }}>
                <span style={{
                  fontSize: '1rem',
                  color: '#2c3e50',
                  fontWeight: '500'
                }}>
                  Want us to visit your school?
                </span>
                <LinkButton href="https://forms.gle/BDF437Bbu1i8gBNT7">
                  Apply
                </LinkButton>
              </div>
            </>
          ) : (
            <div style={{
              backgroundColor: 'transparent',
              padding: '1.5rem',
              borderBottom: '1px solid #ddd',
              textAlign: 'center',
              color: '#6c757d'
            }}>
              No upcoming events
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
};

export default Sidebar;

